const IMAGE_BASE64_PREFIX = "PRINTER_LOGO_IMAGE_BASE64_";
const ESC_POS_BYTES_PREFIX = "PRINTER_LOGO_ESC_POS_BYTES_";

const escPosMemoryCache = new Map();
const IMAGE_LOAD_TIMEOUT = 8000;

function uint8ToBase64(uint8) {
  let binary = "";
  for (let i = 0; i < uint8.length; i++) {
    binary += String.fromCharCode(uint8[i]);
  }
  return btoa(binary);
}

function base64ToUint8(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function fetchImageAsBase64(url) {
  let response;

  try {
    response = await fetch(url);
  } catch (err) {
    throw new Error(`Logo fetch failed: ${err.message}`);
  }

  if (!response.ok) {
    throw new Error(`Logo fetch failed: HTTP ${response.status}`);
  }

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      if (!reader.result) {
        reject(new Error("FileReader returned empty result"));
        return;
      }
      resolve(reader.result);
    };

    reader.onerror = () => reject(new Error("FileReader failed"));

    reader.onabort = () => reject(new Error("FileReader aborted"));

    try {
      reader.readAsDataURL(blob);
    } catch (err) {
      reject(err);
    }
  });
}

export async function getImageBase64(url) {
  const key = IMAGE_BASE64_PREFIX + url;

  const cached = localStorage.getItem(key);
  if (cached) return cached;

  try {
    const base64 = await fetchImageAsBase64(url);
    localStorage.setItem(key, base64);
    return base64;
  } catch (exception) {
    return null;
  }
}

export async function getLogoEscPosBytes(logoUrl) {
  const escPosKey = ESC_POS_BYTES_PREFIX + logoUrl;

  if (escPosMemoryCache.has(logoUrl)) {
    return escPosMemoryCache.get(logoUrl);
  }

  const cached = localStorage.getItem(escPosKey);
  if (cached) {
    const bytes = base64ToUint8(cached);
    escPosMemoryCache.set(logoUrl, bytes);
    return bytes;
  }

  const dataUrl = await getImageBase64(logoUrl);
  if (dataUrl == null) {
    return null;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    let timeoutId;

    const cleanup = () => {
      clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
    };

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error("Logo image load timeout"));
    }, IMAGE_LOAD_TIMEOUT);

    img.onerror = () => {
      cleanup();
      reject(new Error("Logo image failed to load"));
    };

    img.onload = async () => {
      cleanup();

      try {
        const imageBytes = await imageToBytes(img);
        escPosMemoryCache.set(logoUrl, imageBytes);
        localStorage.setItem(escPosKey, uint8ToBase64(imageBytes));

        resolve(imageBytes);
      } catch (err) {
        reject(err);
      }
    };

    img.src = dataUrl;
  });
}

export async function imageToBytes(source) {
  const maxWidth = 576;
  const isCanvas = source instanceof HTMLCanvasElement;
  const sourceWidth = isCanvas ? source.width : source.width;
  const sourceHeight = isCanvas ? source.height : source.height;

  const scale = Math.min(1, maxWidth / sourceWidth);

  const canvas = isCanvas && scale === 1 ? source : document.createElement("canvas");
  if (!(isCanvas && scale === 1)) {
    canvas.width = Math.floor(sourceWidth * scale);
    canvas.height = Math.floor(sourceHeight * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas 2D context unavailable");
    }
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  }

  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  const ctx = canvas.getContext("2d");
  const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight).data;

  const widthBytes = Math.ceil(canvasWidth / 8);
  const height = canvasHeight;

  const bytes = [];
  bytes.push(0x1d, 0x76, 0x30, 0x00);
  bytes.push(widthBytes & 0xff, (widthBytes >> 8) & 0xff);
  bytes.push(height & 0xff, (height >> 8) & 0xff);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < widthBytes * 8; x += 8) {
      let byte = 0;

      for (let bit = 0; bit < 8; bit++) {
        const px = (y * canvasWidth + x + bit) * 4;
        if (px < imageData.length) {
          const avg = (imageData[px] + imageData[px + 1] + imageData[px + 2]) / 3;
          if (avg < 200) byte |= 1 << (7 - bit);
        }
      }
      bytes.push(byte);
    }
  }

  return new Uint8Array(bytes);
}

function wrapColumnTextForCanvas(ctx, colText, colWidthVal) {
  const words = String(colText || "").split("\n");
  const wrappedLines = [];

  words.forEach((word) => {
    let currentLine = "";
    const segments = word.split(" ");
    segments.forEach((segment) => {
      const testLine = currentLine ? `${currentLine} ${segment}` : segment;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > colWidthVal && currentLine) {
        wrappedLines.push(currentLine);
        currentLine = segment;
      } else {
        currentLine = testLine;
      }
    });
    if (currentLine) wrappedLines.push(currentLine);
  });

  return wrappedLines;
}

function renderStructuredRowToCanvas(canvas, ctx, data, options) {
  const { fontSize = 24, fontWeight = "normal", align: defaultAlign = "left", width = 576 } = options;
  const padding = 2;
  const availableWidth = width - padding * 2;
  let maxHeight = 0;
  let currentX = padding;

  const columnData = data.map((col) => {
    const colWidthStr = String(col.width || "100%");
    let colWidthVal = colWidthStr.endsWith("%")
      ? (parseFloat(colWidthStr) / 100) * availableWidth
      : parseFloat(colWidthStr);

    const colAlign = col.align || defaultAlign;
    const colSize = col.size || fontSize;
    const colWeight = col.weight || fontWeight;

    ctx.font = `${colWeight || 600} ${colSize}px monospace`;
    const wrappedLines = wrapColumnTextForCanvas(ctx, col.text, colWidthVal);

    const colHeight = wrappedLines.length * (colSize * 1.4);
    if (colHeight > maxHeight) maxHeight = colHeight;

    const res = { ...col, wrappedLines, colWidthVal, currentX, colAlign, colSize, colWeight };
    currentX += colWidthVal;
    return res;
  });

  const { invert = false } = options;
  canvas.height = maxHeight + padding * 2;
  ctx.fillStyle = invert ? "black" : "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = invert ? "white" : "black";

  columnData.forEach((col) => {
    ctx.font = `${col.colWeight || 600} ${col.colSize}px monospace`;
    ctx.textAlign = col.colAlign;
    ctx.textBaseline = "middle";

    const x =
      col.colAlign === "center"
        ? col.currentX + col.colWidthVal / 2
        : col.colAlign === "right"
          ? col.currentX + col.colWidthVal
          : col.currentX;

    col.wrappedLines.forEach((line, idx) => {
      const colLineHeight = col.colSize * 1.5;
      ctx.fillText(line, x, padding + (idx + 0.5) * colLineHeight);
    });
  });
}

function renderSimpleTextToCanvas(canvas, ctx, data, options) {
  const {
    fontSize = 24,
    fontWeight = "normal",
    align: defaultAlign = "left",
    width = 576,
    lineHeight: customLineHeight,
    invert = false,
  } = options;
  const padding = 2;
  const availableWidth = width - padding * 2;
  const lineHeight = customLineHeight || fontSize * 1.2;

  const text = String(data);
  ctx.font = `${fontWeight || 600} ${fontSize}px monospace`;
  ctx.textAlign = defaultAlign;
  ctx.textBaseline = "middle";

  const words = text.split("\n");
  const lines = [];
  words.forEach((word) => {
    let currentLine = "";
    const segments = word.split(" ");
    segments.forEach((segment) => {
      const testLine = currentLine ? `${currentLine} ${segment}` : segment;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > availableWidth && currentLine) {
        lines.push(currentLine);
        currentLine = segment;
      } else {
        currentLine = testLine;
      }
    });
    if (currentLine) lines.push(currentLine);
  });

  canvas.height = lines.length * lineHeight + padding * 2;
  ctx.fillStyle = invert ? "black" : "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = invert ? "white" : "black";
  ctx.font = `${fontWeight || 600} ${fontSize}px monospace`;
  ctx.textAlign = defaultAlign;
  const x = defaultAlign === "center" ? width / 2 : defaultAlign === "right" ? width - padding : padding;
  lines.forEach((line, index) => {
    ctx.fillText(line, x, padding + (index + 0.5) * lineHeight);
  });
}

export async function textToEscPosBytes(data, options = {}) {
  const { width = 576 } = options;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  if (Array.isArray(data)) {
    renderStructuredRowToCanvas(canvas, ctx, data, options);
  } else {
    renderSimpleTextToCanvas(canvas, ctx, data, options);
  }

  return await imageToBytes(canvas);
}

export async function getQrToBytes(qrData, size = 180) {
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(qrData)}`;

  const dataUrl = await fetchImageAsBase64(url);
  if (dataUrl == null) {
    return null;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    let timeoutId;

    const cleanup = () => {
      clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
    };

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error("QR image load timeout"));
    }, IMAGE_LOAD_TIMEOUT);

    img.onerror = () => {
      cleanup();
      reject(new Error("QR image failed to load"));
    };

    img.onload = async () => {
      cleanup();

      try {
        const qrBytes = await imageToBytes(img);
        resolve(qrBytes);
      } catch (err) {
        reject(err);
      }
    };

    img.src = dataUrl;
  });
}
